import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Legend, PieChart, Pie, Cell
} from 'recharts';
import {
    CreditCard, CheckCircle, AlertTriangle, Clock, Calendar,
    TrendingUp, Activity, Package, Shield, ChevronRight, Download
} from 'lucide-react';

const SubscriptionManagement = () => {
    const [subscription, setSubscription] = useState(null);
    const [currentUsage, setCurrentUsage] = useState(null);
    const [billingHistory, setBillingHistory] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

    // Theme colors
    const COLORS = ['#8B5CF6', '#10B981', '#F59E0B', '#EF4444'];

    useEffect(() => {
        fetchSubscriptionData();
    }, []);

    const fetchSubscriptionData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            // Get institution ID from user data
            const userStr = localStorage.getItem('user');
            const user = userStr ? JSON.parse(userStr) : null;
            const institutionId = user?.institutionId;

            if (!institutionId) {
                // Determine if we should show a friendly "setup" message instead of error
                // For now, let's treat it as a state to prompt re-login
                setError('Institution ID missing. Please relogin to refresh your session.');
                setLoading(false);
                return;
            }

            // Fetch all subscription data with error handling for each
            const [subResponse, packagesResponse, historyResponse] = await Promise.all([
                axios.get(`${API_BASE_URL}/subscriptions/institution/${institutionId}/current`, { headers }).catch(e => ({ data: null })),
                axios.get(`${API_BASE_URL}/subscriptions/packages`, { headers }).catch(e => ({ data: [] })),
                axios.get(`${API_BASE_URL}/subscriptions/institution/${institutionId}/billing-history`, { headers }).catch(e => ({ data: [] }))
            ]);

            // Handle cases where sub is null (404 from backend means no sub)
            let subData = subResponse.data ? (subResponse.data.subscription || subResponse.data) : null;

            setSubscription(subData);
            setPackages(packagesResponse.data || []);
            setBillingHistory(historyResponse.data || []);

            // Calculate current usage only if we have a valid institution
            if (institutionId) {
                const now = new Date();
                const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
                const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

                axios.post(
                    `${API_BASE_URL}/subscriptions/institution/${institutionId}/calculate-bill`,
                    { periodStart, periodEnd },
                    { headers }
                ).then(res => setCurrentUsage(res.data))
                    .catch(err => console.warn('Usage calc failed', err));
            }

        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError('Unable to load subscription details. Please check connection.');
        } finally {
            setLoading(false);
        }
    };

    const getTrialDaysRemaining = () => {
        if (!subscription?.isInTrial || !subscription?.trialEndDate) return 0;
        const now = new Date();
        const endDate = new Date(subscription.trialEndDate);
        const diffTime = endDate - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Prepare chart data from billing history
    const chartData = [...billingHistory].reverse().map(bill => ({
        name: new Date(bill.billingPeriodStart).toLocaleDateString('en-US', { month: 'short' }),
        amount: parseFloat(bill.amountDue || 0),
        students: bill.totalStudents || 0
    }));

    // Loading State
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                <p className="text-gray-500 font-medium">Loading subscription insights...</p>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div className="p-8 max-w-2xl mx-auto mt-10 bg-white rounded-xl shadow-lg border-l-4 border-red-500">
                <div className="flex items-center gap-4 text-red-600 mb-2">
                    <AlertTriangle className="h-6 w-6" />
                    <h3 className="text-lg font-bold">Access Issue</h3>
                </div>
                <p className="text-gray-600">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                >
                    Retry Connection
                </button>
            </div>
        );
    }

    const trialDays = getTrialDaysRemaining();
    const isTrial = subscription?.isInTrial;
    const planName = subscription?.subscription_package?.displayName || (subscription?.packageId === 1 ? 'Basic' : 'Pro'); // Fallback name if relation missing

    return (
        <div className="p-6 w-full mx-auto space-y-8 bg-gray-50 min-h-screen">

            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        <CreditCard className="h-8 w-8 text-indigo-600" />
                        Subscription & Billing
                    </h1>
                    <p className="text-gray-500 mt-1">Manage your institution's plan, billing, and usage limits.</p>
                </div>
                <div className="flex gap-3">
                    <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition shadow-sm font-medium">
                        <Download className="h-4 w-4" />
                        Invoices
                    </button>
                    <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition shadow-md font-medium">
                        Upgrade Plan
                    </button>
                </div>
            </div>

            {/* Top Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

                {/* Status Card */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-bl-full -mr-4 -mt-4 opacity-50"></div>
                    <div>
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Current Status</p>
                        <div className="mt-2 flex items-center gap-2">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${isTrial ? 'bg-blue-100 text-blue-700' :
                                subscription?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                }`}>
                                {isTrial ? 'FREE TRIAL' : (subscription?.status?.toUpperCase() || 'INACTIVE')}
                            </span>
                        </div>
                    </div>
                    <div className="mt-4">
                        <p className="text-2xl font-bold text-gray-900">{planName}</p>
                        <p className="text-sm text-gray-500">{subscription?.autoRenew ? 'Auto-renews monthly' : 'Manual renewal'}</p>
                    </div>
                </div>

                {/* Trial/Bill Countdown */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 relative overflow-hidden">
                    {isTrial ? (
                        <>
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Trial Remaining</p>
                                    <p className="mt-2 text-4xl font-extrabold text-blue-600">{trialDays}</p>
                                    <p className="text-sm text-gray-500">Days left</p>
                                </div>
                                <div className="h-12 w-12 rounded-full border-4 border-blue-100 flex items-center justify-center">
                                    <Clock className="h-5 w-5 text-blue-500" />
                                </div>
                            </div>
                            <div className="mt-4 w-full bg-gray-100 rounded-full h-2">
                                <div
                                    className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${(trialDays / 30) * 100}%` }}
                                ></div>
                            </div>
                            <p className="mt-2 text-xs text-gray-400">Ends {formatDate(subscription?.trialEndDate)}</p>
                        </>
                    ) : (
                        <>
                            <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Next Billing Date</p>
                            <p className="mt-2 text-2xl font-bold text-gray-900">{formatDate(currentUsage?.dueDate || new Date(new Date().setDate(new Date().getDate() + 30)))}</p> {/* Fallback date */}
                            <div className="mt-4 flex items-center gap-2 text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-lg w-fit">
                                <AlertTriangle className="h-4 w-4" />
                                <span>~7 Days remaining</span>
                            </div>
                        </>
                    )}
                </div>

                {/* Estimated Cost */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col justify-between">
                    <div>
                        <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">Est. Amount Due</p>
                        <p className="mt-2 text-3xl font-bold text-gray-900">{formatCurrency(currentUsage?.amountDue || 0)}</p>
                    </div>
                    <div className="mt-4 text-sm text-gray-500">
                        <div className="flex justify-between mb-1">
                            <span>Students Enrolled:</span>
                            <span className="font-medium text-gray-900">{currentUsage?.totalStudents || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Units Taken:</span>
                            <span className="font-medium text-gray-900">{currentUsage?.totalUnits || 0}</span>
                        </div>
                    </div>
                </div>

                {/* Usage Health */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Usage Trends</p>
                    <div className="h-24 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData.length > 0 ? chartData : [{ name: 'Jan', amount: 0 }, { name: 'Feb', amount: 100 }]}>
                                <Line type="monotone" dataKey="amount" stroke="#8B5CF6" strokeWidth={2} dot={false} />
                                <Tooltip />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <p className="text-xs text-gray-400 text-center mt-2">Last 6 months billing</p>
                </div>
            </div>

            {/* Smart Plan Comparison */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Active Plan & Features */}
                <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                        <h2 className="text-lg font-bold text-gray-900">Your Plan Features</h2>
                        <span className="text-indigo-600 text-sm font-medium hover:underline cursor-pointer">Compare Plans</span>
                    </div>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <Package className="h-5 w-5 text-indigo-500" />
                                {planName} Package
                            </h3>
                            <p className="text-gray-500 text-sm mb-6">
                                {subscription?.subscription_package?.description || 'Your current subscription package.'}
                            </p>
                            <div className="space-y-3">
                                {(() => {
                                    let features = [];
                                    const pkgFeatures = subscription?.subscription_package?.features;

                                    if (Array.isArray(pkgFeatures)) {
                                        features = pkgFeatures;
                                    } else if (typeof pkgFeatures === 'string') {
                                        try {
                                            features = JSON.parse(pkgFeatures);
                                        } catch (e) {
                                            console.error('Failed to parse features', e);
                                        }
                                    } else {
                                        // Fallback to finding package from packages list
                                        const matchingPkg = packages.find(p => p.id === subscription?.packageId);
                                        if (matchingPkg?.features) {
                                            features = Array.isArray(matchingPkg.features) ? matchingPkg.features : JSON.parse(matchingPkg.features);
                                        }
                                    }

                                    return (features || []).slice(0, 6).map((feature, idx) => (
                                        <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                                            <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                                            <span>{feature}</span>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                            <h4 className="font-semibold text-gray-900 mb-3">Subscription Limits</h4>
                            <div className="space-y-4">
                                <div>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-gray-600">Student Seats</span>
                                        <span className="font-medium">Unlimited</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className="bg-green-500 h-2 rounded-full w-3/4"></div>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between text-sm mb-1">
                                        <span className="text-gray-600">Storage</span>
                                        <span className="font-medium">50GB / 100GB</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className="bg-indigo-500 h-2 rounded-full w-1/2"></div>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h4 className="font-semibold text-gray-900 mb-2">Billing Rate</h4>
                                <p className="text-3xl font-bold text-gray-900">
                                    {formatCurrency(subscription?.subscription_package?.pricePerStudentPerUnit)}
                                    <span className="text-sm font-normal text-gray-500 ml-1">/ student / unit</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Available Upgrades */}
                <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl shadow-lg text-white p-6 flex flex-col relative overflow-hidden">
                    {/* Background decorations */}
                    <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 rounded-full bg-white opacity-10 blur-xl"></div>
                    <div className="absolute bottom-0 left-0 -ml-8 -mb-8 w-32 h-32 rounded-full bg-purple-400 opacity-20 blur-xl"></div>

                    <div className="relative z-10 flex-1">
                        <div className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs font-bold mb-4 backdrop-blur-sm">
                            RECOMMENDED UPGRADE
                        </div>
                        <h3 className="text-xl font-bold mb-2">Pro Institution Plan</h3>
                        <p className="text-indigo-100 text-sm mb-6">Unlock advanced analytics, live Virtual Classes through Zoom integration, and priority support.</p>

                        <div className="space-y-3 mb-8">
                            <div className="flex items-center gap-2 text-sm text-indigo-50">
                                <CheckCircle className="h-4 w-4 text-green-300" />
                                <span>HD Virtual Classrooms</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-indigo-50">
                                <CheckCircle className="h-4 w-4 text-green-300" />
                                <span>Advanced Smart Analytics</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-indigo-50">
                                <CheckCircle className="h-4 w-4 text-green-300" />
                                <span>Dedicated Success Manager</span>
                            </div>
                        </div>
                    </div>

                    <button className="relative z-10 w-full py-3 bg-white text-indigo-600 rounded-lg font-bold hover:bg-gray-50 transition shadow-lg flex items-center justify-center gap-2">
                        View Pro Features
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Billing History Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                    <h2 className="text-lg font-bold text-gray-900">Billing History</h2>
                    <button className="text-sm text-gray-500 hover:text-indigo-600 font-medium">Download All</button>
                </div>
                {billingHistory.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-gray-500">
                            <thead className="bg-gray-50 border-b border-gray-100 text-xs uppercase text-gray-400 font-semibold">
                                <tr>
                                    <th className="px-6 py-4">Invoice ID</th>
                                    <th className="px-6 py-4">Billing Period</th>
                                    <th className="px-6 py-4">Amount</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-right">Date Paid</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {billingHistory.map((bill) => (
                                    <tr key={bill.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 font-medium text-gray-900">#{bill.id.toString().padStart(6, '0')}</td>
                                        <td className="px-6 py-4">{formatDate(bill.billingPeriodStart)} - {formatDate(bill.billingPeriodEnd)}</td>
                                        <td className="px-6 py-4 font-bold text-gray-900">{formatCurrency(bill.amountDue)}</td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bill.status === 'paid' ? 'bg-green-100 text-green-800' :
                                                bill.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                {bill.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">{bill.paidAt ? formatDate(bill.paidAt) : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-12 text-center text-gray-500 bg-gray-50">
                        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-gray-200 mb-4">
                            <Shield className="h-6 w-6 text-gray-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-900">No Billing History</p>
                        <p>Your billing history will appear here once your trial ends.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionManagement;
